/* eslint-disable react/no-array-index-key */
import type { FilterComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';
import Slider from '@/app/components/search/components/Form/children/Slider';
import type { Target } from '@/app/components/search/components/Form';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import FormatDecorated from '@/app/components/search/components/Filters/verticalFilters/Fields/getPriceField/FormatDecorated.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import styled from 'styled-components';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { usePriceSliderThumbs } from '@/app/common/filters/types/range/usePriceSliderThumbs.ts';
import { getRangeRoundTo, hasDiagram } from '@/app/common/filters/types/range/rangeData.ts';
import type { ReactElement } from 'react';

const Diagram = styled.div`
  height: 50px;
  margin: 0 10px;
  display: flex;
  flex-direction: row;
  gap: 1px;
  align-items: flex-end;
`;

const Column = styled.div`
  background-color: ${({ theme }) => theme.kallisto.color.common.black[100]};
`;

// TODO: Pass in some prop(s) to calculate the width depending on the position of the filter
const getWidth = (isMobile: boolean) => {
  // const { isSidebar, isMobile, isHorizontal } = commonProps;
  const isSidebar = true;
  const isHorizontal = false;

  if (isSidebar) return 260;
  if (isHorizontal) return 268;
  if (!isMobile && !isHorizontal && !isSidebar) return 576;
  if (isMobile && canUseDOM) return window.innerWidth - 64;
  return 0;
};

export const FilterRange = ({
  filter,
  isLoading,
  handleFilterChanged,
  // For callers rendering the slider in a converted unit: keeps committed values round-trippable.
  roundTo: roundToOverride,
  sectionTitle,
}: FilterComponentProps & { roundTo?: number }) => {
  const { disabled } = filter;
  const filterData = filter.data as FilterDataRange;
  const isDisabled = disabled || isLoading;

  const { localization, isMobile } = useFiltersUiData();
  const { t } = useTranslation();
  const thumbs = usePriceSliderThumbs(filter, sectionTitle);

  const handleFilterClicked = (newMin: number, newMax: number) => {
    const updatedFilter = {
      ...filter,
      data: {
        ...filterData,
        value: {
          min: newMin,
          max: newMax,
        },
      },
    };
    handleFilterChanged([updatedFilter]);
  };

  const onUpdate = async (e: Target) => {
    handleFilterClicked(e.target.value.min, e.target.value.max);
  };

  const formatPriceRange = ({
    minValue,
    maxValue,
    maxAllowed,
  }: {
    minValue: number;
    maxValue: number;
    maxAllowed: number;
  }): string | ReactElement => {
    const min = formatPrice({ price: minValue, localization });
    const max =
      maxValue === maxAllowed
        ? t('vacation.oss.components.searchfilters.labels.nolimit')
        : formatPrice({ price: maxValue, localization });

    return (
      <FormatDecorated
        min={min}
        max={max}
        minPrice={t('vacation.oss.filters.budget.min_price')}
        maxPrice={t('vacation.oss.filters.budget.max_price')}
        maxPriceSlider={filterData.maxValue.toString()}
      />
    );
  };

  let sliderProps;
  const commonSliderProps = {
    name: 'price',
    bulletSize: 20,
    className: 'slider-price',
    width: getWidth(isMobile),
    valueLabel: formatPriceRange,
    valuesOnBottom: true,
    thumbNames: thumbs.thumbNames,
    valueText: thumbs.valueText,
    onUpdate,
    roundTo: roundToOverride ?? getRangeRoundTo(filterData),
    value: {
      min: filterData.value.min,
      max: filterData.value.max,
    },
  };

  if (hasDiagram(filterData.dynamicValues)) {
    // ***********
    // * DYNAMIC *
    // ***********
    const { from, to, diagram } = filterData.dynamicValues;
    const maxDiagramItem = Math.max(...diagram);
    const diagramElement = (
      <Diagram>
        {diagram.map((x, i) => {
          let height = x === maxDiagramItem ? 50 : (x * 50) / maxDiagramItem;
          if (height < 4) {
            height = 4;
          }
          return <Column key={`${x}_${i}`} style={{ height, width: `calc(100% / ${diagram.length})` }} />;
        })}
      </Diagram>
    );

    sliderProps = {
      ...commonSliderProps,
      default: {
        min: from,
        max: to,
      },
      minAllowed: from,
      maxAllowed: to,
      diagram: diagramElement,
      ...(isDisabled || from === to ? { disabled: true } : {}),
    };
  } else {
    // **********
    // * STATIC *
    // **********
    sliderProps = {
      ...commonSliderProps,
      default: {
        min: 0,
        max: filterData.maxValue,
      },
      minAllowed: 0,
      maxAllowed: filterData.maxValue,
      ...(isDisabled ? { disabled: true } : {}),
    };
  }

  return <Slider {...sliderProps} />;
};
